import { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { recordBookingPayment } from '../api';

// payements_requirements.md - a rental owner records a payment they collected themselves (cash on
// delivery, or the online gateway was down). The backend restricts an owner to money that reached
// THEM and flags the entry for finance to reconcile.
// fix_issue3 follow-up - the website owner (admin) can also record / correct how and when a
// payment was received, by any means, on any order (they represent the platform). `asAdmin` opens
// the fuller form: the platform-account collection source, and no "flagged for reconciliation".
const OWNER_COLLECTION_SOURCES = [
  { value: 'OWNER_CASH', label: 'Cash, paid to the rental owner in person' },
  { value: 'OWNER_DIRECT_ONLINE', label: 'Online (UPI / bank transfer), direct to the rental owner' }
];
const PLATFORM_COLLECTION_SOURCE = { value: 'PLATFORM_ONLINE', label: 'Online, to the RentalVerse business account' };
const METHODS = [
  { value: 'CASH', label: 'Cash' },
  { value: 'UPI', label: 'UPI' },
  { value: 'BANK_TRANSFER', label: 'Bank transfer' },
  { value: 'CARD', label: 'Card' },
  { value: 'NET_BANKING', label: 'Net banking' },
  { value: 'WALLET', label: 'Wallet' },
  { value: 'OTHER', label: 'Other' }
];

export default function MarkPaymentReceivedModal({ booking, show, onHide, onRecorded, asAdmin = false }) {
  const sources = asAdmin ? [PLATFORM_COLLECTION_SOURCE, ...OWNER_COLLECTION_SOURCES] : OWNER_COLLECTION_SOURCES;
  const [collectionSource, setCollectionSource] = useState(sources[0].value);
  const [method, setMethod] = useState(asAdmin ? 'UPI' : 'CASH');
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [receivedAt, setReceivedAt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const summary = booking?.paymentSummary;
  const orderAmount = summary?.amount != null ? summary.amount : booking?.totalAmount;
  const alreadyReceived = summary?.amountReceived || 0;
  const due = summary?.amountDue;
  // Already settled in full - the caller is only correcting how / when it was received, not
  // logging a new payment (backend treats it as a details correction, no double-count).
  const fullyReceived = orderAmount != null && orderAmount > 0 && alreadyReceived >= orderAmount && !(due > 0);

  useEffect(() => {
    if (!show) return;
    setCollectionSource(sources[0].value);
    setMethod(asAdmin ? 'UPI' : 'CASH');
    setAmount(due != null && due > 0 ? String(due) : (orderAmount != null ? String(orderAmount) : ''));
    setReference('');
    setNote('');
    setReceivedAt('');
    setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, due, orderAmount, asAdmin]);

  if (!booking) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    // A payment is never recorded without a reference - a payment is only "received" with a
    // method AND a transaction reference (for cash, the receipt / acknowledgement number).
    if (reference.trim().length < 2) {
      setError('Enter the payment reference / transaction id (for cash, the receipt or acknowledgement no.).');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const payload = { method, collectionSource, reference: reference.trim(), note: note.trim() };
      if (!fullyReceived && amount !== '') payload.amount = Number(amount);
      if (receivedAt) payload.receivedAt = new Date(receivedAt).toISOString();
      const updated = await recordBookingPayment(booking._id, payload);
      onRecorded(updated);
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to record this payment.');
    } finally {
      setSubmitting(false);
    }
  };

  const money = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title as="h5">{fullyReceived ? 'Edit payment details' : 'Record payment received'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted mb-2">
            Order #{booking._id.slice(-6).toUpperCase()}
            {fullyReceived
              ? ' — this order is already paid in full. Use this only to correct how or when the payment was received (the amount stays the same).'
              : asAdmin
                ? ' — record or correct how and when this payment was received, by any means.'
                : " — use this when you collected the money yourself (cash on delivery, or the online payment couldn't be taken). Finance will reconcile your entry."}
          </p>
          <div className="small mb-3">
            <div><span className="text-muted">Order amount:</span> <strong>{money(orderAmount)}</strong></div>
            <div><span className="text-muted">Received so far:</span> {money(alreadyReceived)}</div>
            {due != null && due > 0 && <div className="text-warning">Still receivable: {money(due)}</div>}
          </div>
          {error && <Alert variant="danger" className="py-2">{error}</Alert>}

          <Form.Group className="mb-3" controlId="pay-received-source">
            <Form.Label>How did the money reach you?</Form.Label>
            <Form.Select value={collectionSource} onChange={(e) => setCollectionSource(e.target.value)}>
              {sources.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </Form.Select>
            {collectionSource !== 'PLATFORM_ONLINE' && (
              <Form.Text className="text-muted">
                The rental owner becomes responsible for refunding this payment if the booking is later cancelled.
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3" controlId="pay-received-method">
            <Form.Label>Payment method</Form.Label>
            <Form.Select value={method} onChange={(e) => setMethod(e.target.value)}>
              {METHODS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </Form.Select>
          </Form.Group>

          {!fullyReceived && (
            <Form.Group className="mb-3" controlId="pay-received-amount">
              <Form.Label>Amount received (₹)</Form.Label>
              <Form.Control
                type="number" min="0" step="0.01" value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={orderAmount != null ? String(orderAmount) : 'Full amount'}
              />
            </Form.Group>
          )}

          <Form.Group className="mb-3" controlId="pay-received-at">
            <Form.Label>Date received (optional — leave blank for today)</Form.Label>
            <Form.Control type="date" value={receivedAt} onChange={(e) => setReceivedAt(e.target.value)} />
          </Form.Group>

          <Form.Group className="mb-3" controlId="pay-received-reference">
            <Form.Label>
              {method === 'CASH' ? 'Cash receipt / acknowledgement no.' : 'Reference / transaction id (UPI ref, bank UTR, auth code)'}
              {' '}<span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text" required minLength={2} maxLength={200} value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
            <Form.Text className="text-muted">
              Required — a payment is only recorded as received with a method and a reference.
            </Form.Text>
          </Form.Group>

          <Form.Group controlId="pay-received-note">
            <Form.Label>Note (optional)</Form.Label>
            <Form.Control as="textarea" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={submitting}>Cancel</Button>
          <Button variant="success" type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : fullyReceived ? 'Save payment details' : 'Confirm payment received'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
